import { TeamRecord, Competition } from '../interfaces/Competition';
import { ListProvider } from '../interfaces/ListProvider';
import { MatchSegmentStats } from '../interfaces/Stat';

import { SportProvider } from '../interfaces/SportProvider';
import axios from 'axios';

export function isMatchStats(
    candidate: MatchSegmentStats[] | string
): candidate is MatchSegmentStats[] {
    return (candidate as MatchSegmentStats[]).length !== undefined;
}

export default class FirebaseBackedCompetitionStandingsProvider
    implements ListProvider<TeamRecord>
{
    sportProvider: SportProvider;
    competition: Competition;

    constructor(
        database: any,
        competition: Competition,
        sportProvider: SportProvider
    ) {
        this.competition = competition;
        this.sportProvider = sportProvider;
    }

    addListener(callback) {
        const promise = this.once();
        promise
            .then((teamRecords) => {
                callback(teamRecords);
            })
            .catch((error) => {
                callback([]);
            });
    }

    removeListener(token) {}

    once() {
        // Use API endpoint instead of direct Firebase access
        var apiPath = '/api/competitions/display/teamrecords';
        if (process.env.REACT_APP_FIREBASE_KEY === 'development') {
            apiPath = 'http://localhost:3000' + apiPath;
        }

        return axios
            .post(apiPath, { key: this.competition.id })
            .then((response) => {
                if (response.status === 200) {
                    // API returns TeamRecord[] directly, but we need to resolve team objects
                    const apiRecords = response.data as Array<{
                        team: { id: string; name: string };
                        wins: number;
                        losses: number;
                        draws: number;
                    }>;

                    return this.competition.teamProvider.once().then((competitionEntryResolvers) => {
                        const competitionEntryMap = competitionEntryResolvers.reduce(
                            (currentMap, competitionEntryResolver) => {
                                currentMap[competitionEntryResolver.id] = competitionEntryResolver;
                                return currentMap;
                            },
                            {}
                        );

                        const teamRecordPromises = apiRecords.map(async (apiRecord) => {
                            const entryResolver = competitionEntryMap[apiRecord.team.id];
                            if (entryResolver) {
                                const matchEntry = await entryResolver.asAPromise();
                                return {
                                    team: matchEntry,
                                    wins: apiRecord.wins,
                                    losses: apiRecord.losses,
                                    draws: apiRecord.draws,
                                };
                            }
                            // If resolver not found, return basic record with team info from API
                            return {
                                team: {
                                    id: apiRecord.team.id,
                                    name: apiRecord.team.name,
                                } as any,
                                wins: apiRecord.wins,
                                losses: apiRecord.losses,
                                draws: apiRecord.draws,
                            };
                        });

                        return Promise.allSettled(teamRecordPromises).then((teamRecordResults) => {
                            const teamRecords: TeamRecord[] = [];
                            teamRecordResults.forEach((result) => {
                                if (result.status === 'fulfilled') {
                                    teamRecords.push(result.value);
                                }
                            });
                            return teamRecords.sort((left, right) => {
                                const winDiff = right.wins - left.wins;
                                if (winDiff !== 0) {
                                    return winDiff;
                                } else {
                                    return left.losses - right.losses;
                                }
                            });
                        });
                    });
                } else {
                    return Promise.reject(`Unexpected status: ${response.status}`);
                }
            })
            .catch((error) => {
                console.error('Error fetching team records:', error);
                return Promise.reject(error);
            });
    }
}
